import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../HospitalDashboard.css';
import './HospitalUserCredentials.css';
import { fetchHospitalUserCredentials } from '../../../helper/HospitalUserCredentialsHelper';
import ForgotPasswordModal from './ForgotPasswordModal';
import ResultModal from './ResultModal';

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const HospitalUserCredentials = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const hospitalId = queryParams.get('hospitalId');
  const hospitalName = location.state?.hospitalName;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [resultModal, setResultModal] = useState(null);

  const loadUsers = () => {
    if (!hospitalId) {
      setFetchError('Hospital ID is missing');
      setLoading(false);
      return;
    }

    setLoading(true);
    setFetchError('');

    fetchHospitalUserCredentials(Number(hospitalId))
      .then((response) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        setUsers(list);
      })
      .catch((err) => {
        setUsers([]);
        setFetchError(typeof err === 'string' ? err : 'Failed to load user credentials');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadUsers();
  }, [hospitalId]);

  const handlePasswordUpdated = (message) => {
    setSelectedUser(null);
    setResultModal({
      title: 'Success',
      message: message || 'Password updated successfully!',
      variant: 'success',
    });
  };

  return (
    <div className="hospital-page">
      <nav className="hospital-breadcrumb" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate('/hospital-details/hospital-dashboard/')}>
          Hospitals
        </button>
        <span className="hospital-breadcrumb-sep">›</span>
        <button
          type="button"
          onClick={() => navigate(`/hospital-details/wards?hospitalId=${hospitalId}`)}
        >
          {displayValue(hospitalName)}
        </button>
        <span className="hospital-breadcrumb-sep">›</span>
        <span>User Credentials</span>
      </nav>

      <div className="hospital-header">
        <h3 className="hospital-title">Hospital User Credentials</h3>
      </div>

      <div className="hospital-stats-grid">
        <div className="hospital-stat-card hospital-stat-card--total">
          <span className="hospital-stat-label">TOTAL USERS</span>
          <span className="hospital-stat-value">{users.length}</span>
        </div>
      </div>

      <div className="hospital-toolbar">
        <h2 className="hospital-section-title">User List</h2>
      </div>

      {loading ? (
        <div className="hospital-credentials-loading">Loading users...</div>
      ) : fetchError ? (
        <div className="hospital-empty">{fetchError}</div>
      ) : users.length === 0 ? (
        <div className="hospital-empty">No users found for this hospital.</div>
      ) : (
        <div className="hospital-credentials-table-wrap">
          <table className="hospital-credentials-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Forget Password</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{displayValue(user.id)}</td>
                  <td>{displayValue(user.name)}</td>
                  <td>{displayValue(user.email)}</td>
                  <td>
                    <button
                      type="button"
                      className="hospital-credentials-forget-btn"
                      onClick={() => setSelectedUser(user)}
                    >
                      Forget Password
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedUser ? (
        <ForgotPasswordModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSuccess={handlePasswordUpdated}
        />
      ) : null}

      {resultModal ? (
        <ResultModal
          title={resultModal.title}
          message={resultModal.message}
          variant={resultModal.variant}
          onClose={() => setResultModal(null)}
        />
      ) : null}
    </div>
  );
};

export default HospitalUserCredentials;
